import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { http } from "@/lib/api";
import { getSessionId, streamSSE } from "@/lib/stream";

const GREETING = { role: "assistant", content: "Olá! Sou a assistente da Mãos Arcana. Posso explicar as leituras, os valores e como funciona o pagamento via Pix." };
const SUGGESTIONS = ["Quanto custa uma leitura?", "Como funciona o Pix?", "Qual a diferença da meia hora?"];

const Bubble = ({ m, i }) => (
  <div data-testid={`chat-msg-${i}`} className={`max-w-[85%] whitespace-pre-line rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "ml-auto bg-[#CBA365] text-[#080312]" : "border border-white/10 bg-white/5 text-white/85"}`}>
    {m.content || <span className="animate-pulse text-[#CBA365]">…</span>}
  </div>
);

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    http.get(`/ai/chat/${getSessionId()}`).then(({ data }) => {
      if (Array.isArray(data) && data.length) setMsgs([GREETING, ...data]);
    }).catch(() => {});
  }, [open]);

  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [msgs]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    const append = (t) => setMsgs((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + t };
      return copy;
    });
    await streamSSE("/ai/chat", { session_id: getSessionId(), message: msg }, (ev) => {
      if (ev?.type === "delta") append(ev.text ?? "");
      if (ev?.type === "error") append(ev.text ?? "");
    });
    setBusy(false);
  };

  return (
    <>
      <motion.button data-testid="chat-toggle-btn" onClick={() => setOpen(!open)} whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.95 }}
        className="fixed bottom-5 right-5 z-[70] flex h-14 w-14 items-center justify-center rounded-full bg-[#CBA365] text-[#080312] shadow-[0_0_40px_rgba(203,163,101,0.45)]"
        aria-label={open ? "Fechar chat" : "Abrir chat"}>
        {open ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
      </motion.button>
      <AnimatePresence>
        {open && (
          <motion.div data-lenis-prevent data-testid="chat-panel" initial={{ opacity: 0, y: 24, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-3 bottom-24 z-[70] flex h-[70vh] max-h-[560px] flex-col overflow-hidden rounded-2xl border border-[#CBA365]/30 bg-[#0d0519]/95 backdrop-blur-xl sm:inset-x-auto sm:right-5 sm:w-[380px]">
            <div className="border-b border-white/10 px-5 py-4">
              <p className="text-xs uppercase tracking-[0.3em] text-[#CBA365]">Atendimento</p>
              <p className="font-display text-2xl text-[#F5F2EB]">Tire suas dúvidas</p>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {msgs.map((m, i) => <Bubble key={i} m={m} i={i} />)}
              {msgs.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={s} data-testid={`chat-suggestion-${i}`} onClick={() => send(s)}
                      className="rounded-full border border-[#CBA365]/40 px-3 py-1.5 text-xs text-[#CBA365] transition-colors duration-300 hover:bg-[#CBA365]/10">{s}</button>
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t border-white/10 p-3">
              <input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)} maxLength={500} placeholder="Escreva sua dúvida…"
                className="min-w-0 flex-1 rounded-full border border-white/15 bg-white/5 px-4 text-sm text-[#F5F2EB] placeholder:text-white/45 focus:outline-none focus:ring-1 focus:ring-[#CBA365]" />
              <button data-testid="chat-send-btn" disabled={busy} className="flex h-11 w-11 items-center justify-center rounded-full bg-[#CBA365] text-[#080312] disabled:opacity-50" aria-label="Enviar">
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
